// inv/src/app/app-components/deals-modules/IndustryCategoryPicker.jsx
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchIndustryCategories } from './apiService';
import { Button } from '../../../components/ui/button';
import { InputSearch } from '../../../components/ui/inputSearch'; // <-- your component
import { X } from 'lucide-react';

const IndustryCategoryPicker = ({
    selectedCategories = [],
    setSelectedCategories,
    placeholder = 'Search industry...',
    disabled = false,
}) => {
    const { t } = useTranslation();
    const [all, setAll] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load categories once (apiService already has a cache)
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        fetchIndustryCategories()
            .then((cats) => { if (mounted) setAll(cats || []); })
            .catch(() => { if (mounted) setAll([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    // Available options = all minus already selected
    const options = useMemo(() => {
        const picked = new Set(selectedCategories.map(s => s.toLowerCase()));
        return (all || []).filter(c => !picked.has(String(c).toLowerCase()));
    }, [all, selectedCategories]);

    const addCategory = (cat) => {
        if (!cat) return;
        if (selectedCategories.some(c => c.toLowerCase() === cat.toLowerCase())) return;
        setSelectedCategories([...selectedCategories, cat]);
    };

    const removeCategory = (cat) => {
        setSelectedCategories(selectedCategories.filter(c => c !== cat));
    };

    const clearAll = () => setSelectedCategories([]);

    return (
        <div className="space-y-2">
            {/* Search/add: always keep selectedValue="" so the field does not become a single chip */}
            <InputSearch
                options={options}
                selectedValue=""
                onChange={addCategory}
                placeholder={placeholder}
                className="w-full"
                disabled={disabled || loading}
            />

            {/* Selected categories */}
            <div className="flex flex-wrap gap-2">
                {selectedCategories.length === 0 ? (
                    <span className="text-sm text-gray-500">
                        {loading ? 'Loading…' : t('app:deals.t12')}
                    </span>
                ) : (
                    selectedCategories.map((cat) => (
                        <span
                            key={cat}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-sm bg-stone-700 text-white"
                            title={cat}
                        >
                            {cat}
                            <button
                                type="button"
                                onClick={() => removeCategory(cat)}
                                className="cursor-pointer ml-1 hover:opacity-80"
                                aria-label={`Remove ${cat}`}
                            >
                                <X size={14} />
                            </button>
                        </span>
                    ))
                )}
            </div>

            {selectedCategories.length > 0 && (
                <Button
                    variant="link"
                    size="xs"
                    onClick={clearAll}
                    className="text-red-500 hover:text-red-700"
                >
                    {t('app:deals.t13')}
                </Button>
            )}
        </div>
    );
};

export default IndustryCategoryPicker;
